"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const formatter_1 = __importDefault(require("@powerednow/shared/modules/utilities/formatter"));
const complexData_1 = __importDefault(require("@powerednow/complexData/complexData"));
const constants_1 = require("@powerednow/shared/constants");
const modelLookup_1 = require("../modelLookup");
const entity_1 = __importDefault(require("./entity"));
const modelProperties_1 = __importDefault(require("./modelProperties"));
// eslint-disable-next-line no-redeclare
class Address extends complexData_1.default {
    static get allowedAssociations() {
        return {
            site: {
                key: 'contactToAddress',
                instance: (0, modelLookup_1.complexFindModel)('ContactToAddress'),
                cascadeDelete: false,
                condition: {
                    customer_id: this.Entity.getFieldSymbols().customer_id,
                    address_id: this.Entity.getFieldSymbols().id,
                },
            },
            country: {
                key: 'country',
                instance: (0, modelLookup_1.complexFindModel)('Country'),
                single: true,
                cascadeDelete: false,
                condition: {
                    code: this.Entity.getForeignFieldSymbols().country,
                },
            },
            userToAddress: {
                key: 'userToAddress',
                instance: (0, modelLookup_1.complexFindModel)('UserToAddress'),
                single: true,
                cascadeDelete: false,
                condition: {
                    address_id: this.Entity.getFieldSymbols().id,
                },
            },
        };
    }
    /**
     * Extra initialisation - should be called after the object has been added to the DataWrapper events
     */
    async initDefaultAssociatedItems() {
        await super.initDefaultAssociatedItems();
        const companyCountry = await (0, modelLookup_1.complexFindModel)('Company').getCompanyCountry(this);
        this.data.country = this.data.country || (companyCountry && companyCountry.code);
    }
    async isUserAddress() {
        const userToAddress = await this.getUserToAddress();
        return Boolean(userToAddress);
    }
    async isBelongToUser(user) {
        const userToAddress = await this.getUserToAddress();
        return Boolean(userToAddress && userToAddress.data.user_id === user.data.id);
    }
    /**
     * Validation rules that can't be handled by
     *
     * @param params - object that holds information about the current validation environment (e.g. global objects, like current user)
     */
    async validateCustomRules(params) {
        const validationResult = await super.validateCustomRules(params);
        const { currentUser } = params.globalObjects;
        let isValid = true;
        const isUserAddress = await this.isUserAddress();
        if (isUserAddress) {
            const currentUserCanManageCompanyData = await currentUser.hasRole(constants_1.ROLES.PERMISSIONS.CAN_MANAGE_COMPANY_DATA);
            const currentUserCanManageOwnDetails = await currentUser.hasRole(constants_1.ROLES.PERMISSIONS.CAN_EDIT_OWN_ACCOUNT_DETAILS);
            isValid = currentUserCanManageCompanyData || (await this.isBelongToUser(currentUser) && currentUserCanManageOwnDetails);
        }
        else {
            isValid = await currentUser.hasRole(constants_1.ROLES.PERMISSIONS.CAN_EDIT_CUSTOMERS);
        }
        if (!isValid) {
            validationResult.errors.push(constants_1.VALIDATION.CODES.ADDRESS.NO_CHANGE_PERMISSION);
        }
        if (this.data.what_three_words) {
            const whatThreeWordsValid = /[a-z]+[.][a-z]+[.][a-z]+/.test(this.data.what_three_words);
            if (!whatThreeWordsValid) {
                validationResult.errors.push(constants_1.VALIDATION.CODES.ADDRESS.INVALID_WHAT_THREE_WORDS);
            }
        }
        return validationResult;
    }
    getFileName() {
        return 'x';
    }
    getFormattedSiteAddress(plot = null) {
        const addressPlot = plot ? `${plot} ` : '';
        const { address1 } = this.data;
        const { address2 } = this.data;
        const { postcode, county, state, city, } = this.data;
        let delimiter = ' ';
        if (address1 && address2 && !Number.isInteger(Number(address1))) {
            delimiter = ', ';
        }
        return formatter_1.default.stringifyAddress({
            address: {
                address2,
                plottedAddress1: formatter_1.default.stringifyAddress({
                    address: {
                        addressPlot,
                        address1,
                    },
                }, {
                    addressPlot: 0,
                    address1: 1,
                }, ', '),
                postcode,
                county,
                state,
                city,
            },
        }, {
            plottedAddress1: 0,
            address2: 1,
            city: 2,
            state: 3,
            county: 4,
            postcode: 5,
        }, delimiter);
    }
    getFormattedAddressLines(plot = null) {
        const addressPlot = plot ? `${plot} ` : '';
        const { address1 } = this.data;
        const { address2 } = this.data;
        let delimiter = '';
        if (address1 && address2) {
            delimiter = Number.isInteger(Number(address1)) ? ' ' : '<br />';
        }
        return formatter_1.default.stringifyAddress({
            address: {
                address2,
                plottedAddress1: formatter_1.default.stringifyAddress({
                    address: {
                        addressPlot,
                        address1,
                    },
                }, {
                    addressPlot: 0,
                    address1: 1,
                }, '<br />'),
            },
        }, {
            plottedAddress1: 0,
            address2: 1,
        }, delimiter);
    }
    async getTemplateData(plot = null) {
        const country = await this.getCountry();
        const data = {
            ...this.data.getPureDataValues(),
            addressPlot: plot,
            countryName: country ? country.data.name : '',
            countryId: country ? country.data.id : -1,
            addressMerged: this.getFormattedAddressLines(plot),
            formattedAddress: '',
        };
        data.formattedAddress = formatter_1.default.stringifyAddress({
            plot,
            address: data,
            ...(country ? { country: country.data.name } : {}),
        });
        return data;
    }
    async stringifyAddress(plot, includeCountry, format) {
        return formatter_1.default.stringifyAddress(await this.getDataForFormatter(plot, includeCountry), format);
    }
    async getDataForFormatter(plot = null, includeCountry = true) {
        const country = includeCountry ? await this.getCountry() : null;
        return {
            address: this.data.getPureDataValues(),
            ...(country ? { country: country.data.name } : {}),
            ...(plot ? { plot } : {}),
        };
    }
    async isFilled(addCountry = false) {
        return formatter_1.default.stringifyAddress({
            address: this.data.getPureDataValues(),
            ...(addCountry ? {
                country: (await this.getCountry()).data.name,
            } : {}),
        }).trim() !== '';
    }
    async getSearchableFields() {
        return [
            this.data.address1,
            this.data.address2,
            this.data.city,
            this.data.state,
            this.data.county,
            this.data.postcode,
        ];
    }
    getPropertiesToCopy() {
        const { id, isdefault, invoice, customer_id, company_id, is_registered, is_invoice, ...addressData } = this.data.getPureDataValues();
        return addressData;
    }
    async copyData(item) {
        Object.assign(this.data, item.getPropertiesToCopy());
        return this;
    }
    async findCountryId() {
        const country = await this.getCountry();
        if (!country) {
            return constants_1.COUNTRY.IDS.UNITED_KINGDOM;
        }
        return country.data.id;
    }
}
Address.Entity = entity_1.default;
Address.modelProperties = modelProperties_1.default;
exports.default = Address;
//# sourceMappingURL=../../../../src/models/address/index.js.map